// Persistence and image export. Under Tauri these use native save/open dialogs
// and real file paths; in a plain browser they fall back to <a download> and
// <input type=file>. PDF export is hand-built (a single FlateDecode image) so no
// runtime dependency is needed; deflate comes from the platform CompressionStream.
//
// Export failures throw an Error whose message is a stable code (png-encode /
// canvas-unavailable / svg-rasterize), matching what the Rust shell returns: core stays
// language-independent and the UI localizes the code (src/i18n error.shell).

import {
  isTauri,
  nativeOpenPath,
  nativeReadBinary,
  nativeReadText,
  nativeSavePath,
  nativeWriteBinary,
  nativeWriteText,
} from "./platform";
import type { FileFilter } from "./platform";
import { pipeBytes, stripXmlInvalid } from "./plan";

/** Outcome of a save: not saved means the user canceled the native dialog. */
export interface SaveResult {
  saved: boolean;
  /** Present only when a native dialog returned a real path (Tauri). */
  path?: string;
}

/** Outcome of an open: text plus, under Tauri, the real source path. */
export interface OpenResult {
  text: string;
  path?: string;
}

export function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: "application/json" });
  triggerDownload(blob, filename);
}

/** Save text to a native-chosen path (Tauri) or download it (browser). */
export async function saveTextDocument(defaultName: string, text: string, filter: FileFilter): Promise<SaveResult> {
  if (isTauri()) {
    const path = await nativeSavePath(defaultName, filter);
    if (!path) return { saved: false };
    await nativeWriteText(path, text);
    return { saved: true, path };
  }
  downloadText(defaultName, text);
  return { saved: true };
}

/** Open text from a native dialog (Tauri) or a file-input picker (browser). */
export async function openTextDocument(filter: FileFilter): Promise<OpenResult | null> {
  if (isTauri()) {
    const path = await nativeOpenPath(filter);
    if (!path) return null;
    return { text: await nativeReadText(path), path };
  }
  const text = await pickTextFile();
  return text == null ? null : { text };
}

/** Read a previously-used file by its path (Tauri only; used for recent plans and
 *  by a desktop drag & drop, which carries real paths). */
export function readTextByPath(path: string): Promise<string> {
  return nativeReadText(path);
}

/** The binary counterpart (Tauri only), so a settings file read from a dropped
 *  path and one read from the dialog take the same route. */
export function readBinaryByPath(path: string): Promise<Uint8Array> {
  return nativeReadBinary(path);
}

/** Outcome of a binary open: the bytes plus the source path. */
export interface OpenBinaryResult {
  bytes: Uint8Array;
  path: string;
}

/** Open a binary file from a native dialog. Desktop only — the one caller (the
 *  experimental .urxf import) is itself desktop-only, and a browser has no path
 *  to read back. Returns null when canceled or outside Tauri. */
export async function openBinaryDocument(filter: FileFilter): Promise<OpenBinaryResult | null> {
  if (!isTauri()) return null;
  const path = await nativeOpenPath(filter);
  if (!path) return null;
  return { bytes: await nativeReadBinary(path), path };
}

async function saveBlob(defaultName: string, blob: Blob, filter: FileFilter): Promise<SaveResult> {
  if (isTauri()) {
    const path = await nativeSavePath(defaultName, filter);
    if (!path) return { saved: false };
    await nativeWriteBinary(path, new Uint8Array(await blob.arrayBuffer()));
    return { saved: true, path };
  }
  triggerDownload(blob, defaultName);
  return { saved: true };
}

/** Resolves the file's text, or null when the operator canceled. A read that FAILS
 *  rejects instead: null means "no file was chosen", and letting a failed read take
 *  that seat made an unreadable file indistinguishable from a dismissed picker —
 *  no status line, no dialog, nothing. The Tauri path already surfaces the same
 *  failure as a rejected `read_text_file`, and every caller of this one is inside a
 *  try/catch that reports the reason. Browser build only (the demo has no file IO). */
function pickTextFile(): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const input = document.getElementById("file-input") as HTMLInputElement | null;
    if (!input) return resolve(null);
    const cleanup = (): void => {
      input.removeEventListener("change", onChange);
      input.removeEventListener("cancel", onCancel);
    };
    const onChange = (): void => {
      cleanup();
      const file = input.files?.[0];
      input.value = "";
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error("file read failed"));
      reader.readAsText(file);
    };
    // Canceling the picker fires "cancel", never "change", so without this the
    // promise never settles and the shared file latch wedges. Resolve to null,
    // matching the "read resolves null on cancel" contract of the native path.
    const onCancel = (): void => {
      cleanup();
      input.value = "";
      resolve(null);
    };
    input.addEventListener("change", onChange);
    input.addEventListener("cancel", onCancel);
    input.click();
  });
}

export interface ExportOptions {
  width: number;
  height: number;
  scale?: number;
  /** Raster background color. Absent = the active theme's --canvas-bg. */
  background?: string;
}

export async function exportSvgToPng(
  svg: SVGSVGElement,
  filename: string,
  opts: ExportOptions,
  filter: FileFilter,
): Promise<SaveResult> {
  const canvas = await rasterizeSvg(svg, opts);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  // saved:false is the signal for "the user canceled the dialog". A null blob is
  // an encode failure, so throwing keeps it out of that signal and lets the
  // caller show it as the failure it is.
  if (!blob) throw new Error("png-encode");
  return saveBlob(filename, blob, filter);
}

export async function exportSvgToPdf(
  svg: SVGSVGElement,
  filename: string,
  opts: ExportOptions,
  filter: FileFilter,
): Promise<SaveResult> {
  const canvas = await rasterizeSvg(svg, opts);
  const ctx = canvas.getContext("2d");
  // saved:false is the "user canceled the dialog" signal (see exportSvgToPng). A
  // missing context is an internal failure, so throw rather than mis-signal it as
  // a cancel — rasterizeSvg already obtained one, so this is unreachable anyway.
  if (!ctx) throw new Error("canvas-unavailable");
  const px = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const deflated = await deflate(dropAlpha(px.data));
  const blob = buildImagePdf(deflated, canvas.width, canvas.height, opts.width, opts.height);
  return saveBlob(filename, blob, filter);
}

/** Render an SVG element onto a canvas, filling the active canvas background. The serialized
 *  markup loses every code point XML refuses first: the serializer writes one out as it is, and
 *  the image then fails to load as a whole. */
function rasterizeSvg(svg: SVGSVGElement, opts: ExportOptions): Promise<HTMLCanvasElement> {
  const { width, height } = opts;
  const scale = opts.scale ?? 2;
  const xml = stripXmlInvalid(new XMLSerializer().serializeToString(svg));
  const svgUrl = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = (): void => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext("2d");
      URL.revokeObjectURL(svgUrl);
      if (!ctx) return reject(new Error("canvas-unavailable"));
      const bg =
        opts.background ?? (getComputedStyle(document.body).getPropertyValue("--canvas-bg").trim() || "#0f1115");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = (): void => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error("svg-rasterize"));
    };
    img.src = svgUrl;
  });
}

/** RGBA → packed RGB (the canvas is already composited over an opaque bg). */
function dropAlpha(rgba: Uint8ClampedArray): Uint8Array<ArrayBuffer> {
  const rgb = new Uint8Array((rgba.length / 4) * 3);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
    rgb[j] = rgba[i];
    rgb[j + 1] = rgba[i + 1];
    rgb[j + 2] = rgba[i + 2];
  }
  return rgb;
}

/** zlib deflate via the platform CompressionStream (PDF FlateDecode format). */
function deflate(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  return pipeBytes(bytes, new CompressionStream("deflate"));
}

/** Minimal single-page PDF holding one full-bleed DeviceRGB image. */
function buildImagePdf(deflated: Uint8Array, pxW: number, pxH: number, ptW: number, ptH: number): Blob {
  const w = Math.round(ptW);
  const h = Math.round(ptH);
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (s: string | Uint8Array): void => {
    const b = typeof s === "string" ? enc.encode(s) : s;
    parts.push(b);
    length += b.length;
  };
  const obj = (): void => {
    offsets.push(length);
  };

  push("%PDF-1.4\n");
  obj();
  push("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
  obj();
  push("2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n");
  obj();
  push(
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] ` +
      `/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`,
  );
  obj();
  push(
    `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pxW} /Height ${pxH} ` +
      `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode ` +
      `/Length ${deflated.length} >>\nstream\n`,
  );
  push(deflated);
  push("\nendstream\nendobj\n");
  const content = enc.encode(`q ${w} 0 0 ${h} 0 0 cm /Im0 Do Q\n`);
  obj();
  push(`5 0 obj\n<< /Length ${content.length} >>\nstream\n`);
  push(content);
  push("endstream\nendobj\n");

  const xrefAt = length;
  const size = offsets.length + 1;
  let xref = `xref\n0 ${size}\n0000000000 65535 f \n`;
  for (const off of offsets) xref += `${String(off).padStart(10, "0")} 00000 n \n`;
  push(xref);
  push(`trailer\n<< /Size ${size} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`);

  return new Blob(parts as BlobPart[], { type: "application/pdf" });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// --- localStorage JSON helpers ---------------------------------------------

/** Read a JSON value from localStorage, returning `fallback` on a miss or any
 *  parse / storage error (private mode, disabled storage). */
export function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Write a JSON value to localStorage, ignoring quota / disabled-storage errors. */
export function saveJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / disabled storage
  }
}

// --- recent plans (Tauri only: needs real file paths) ----------------------

export interface RecentEntry {
  path: string;
  name: string;
  modelId: string;
}

const RECENT_KEY = "urx-recent";
const RECENT_MAX = 8;

/** Final path segment, handling both POSIX and Windows separators. */
export function baseName(path: string): string {
  return path.split(/[\\/]/).pop() || path;
}

export function loadRecent(): RecentEntry[] {
  const list = loadJson<unknown>(RECENT_KEY, []);
  if (!Array.isArray(list)) return [];
  return list.filter(
    (e): e is RecentEntry =>
      !!e && typeof e.path === "string" && typeof e.name === "string" && typeof e.modelId === "string",
  );
}

/** Record a just-used path at the front, de-duplicated and capped. */
export function rememberRecent(entry: RecentEntry, max = RECENT_MAX): RecentEntry[] {
  const next = [entry, ...loadRecent().filter((e) => e.path !== entry.path)].slice(0, max);
  saveJson(RECENT_KEY, next);
  return next;
}

/** Re-cap the stored list (the recent-length preference changed; 0 = the
 *  Preferences clear action). */
export function trimRecent(max: number): RecentEntry[] {
  const next = loadRecent().slice(0, max);
  saveJson(RECENT_KEY, next);
  return next;
}

/** Drop one path from the list — a recent entry whose file failed to load
 *  (moved / deleted / corrupted) would only reproduce the same error. */
export function removeRecent(path: string): RecentEntry[] {
  const next = loadRecent().filter((e) => e.path !== path);
  saveJson(RECENT_KEY, next);
  return next;
}
